"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { t } from "@lingui/core/macro";
import { useNotificationSync, type NotificationPayload } from "@/hooks/use-notification-sync";
import useLocalizedRouter from "@/hooks/use-localized-router";
import { decode } from "html-entities";

const TOAST_DURATION_MS = 10_000;
const TOAST_FADE_MS = 300;

function NotificationManager() {
  const router = useLocalizedRouter();

  const [toastContent, setToastContent] = useState<{ title: string; message: string } | null>(null);
  const [toastFading, setToastFading] = useState(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeToast = useCallback(() => {
    setToastFading(true);
    fadeTimerRef.current = setTimeout(() => setToastContent(null), TOAST_FADE_MS);
  }, []);

  const handleNotification = useCallback(
    ({ title, message }: NotificationPayload) => {
      if (fadeTimerRef.current !== null) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
      if (dismissTimerRef.current !== null) {
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
      setToastContent({ title, message });
      setToastFading(false);
      dismissTimerRef.current = setTimeout(closeToast, TOAST_DURATION_MS);
    },
    [closeToast],
  );

  useNotificationSync({ onNotification: handleNotification });

  useEffect(() => {
    return () => {
      if (fadeTimerRef.current !== null) clearTimeout(fadeTimerRef.current);
      if (dismissTimerRef.current !== null) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  if (!toastContent) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        right: { xs: "6%", md: "2%" },
        maxWidth: "380px",
        top: { xs: "6%", md: "4%" },
        zIndex: 2000,
        ml: "15px",
      }}
    >
      <Alert
        severity="info"
        className={`transition-transform duration-300 ${toastFading ? "slide-out" : "slide-in"}`}
        sx={{
          ml: "auto",
          width: "fit-content",
          borderLeft: "5px solid",
          borderColor: "#19a1b3",
          bgcolor: "#fff",
          color: "#19a1b3",
          zIndex: 20,
          position: "relative",
          "& .MuiAlert-icon": {
            alignItems: "center",
            display: "flex",
          },
        }}
        onClose={closeToast}
      >
        <div
          className="cursor-pointer"
          onClick={() => {
            closeToast();
            router.push("/notifications");
          }}
        >
          {toastContent.title ? (
            <Typography component="span" sx={{ display: "block", fontWeight: 400 }}>
              {toastContent.title}
            </Typography>
          ) : null}
          <Typography component="span" sx={{ display: "block", fontWeight: 400, fontSize: "14px" }}>
            {decode(toastContent.message) || t`You have a new notification`}
          </Typography>
        </div>
      </Alert>
    </Box>
  );
}

export default NotificationManager;
